// more info regarding the technique of multiplexing a single threejs canvas be found here:
// https://r105.threejsfundamentals.org/threejs/lessons/threejs-multiple-scenes.html

import React, { useState, useCallback, useEffect, createContext } from 'react';

import {
  Clock,
  WebGLRenderer,
  Color,
  // sRGBEncoding
} from 'three';

import s from './ThreeJsMultiplexedCanvasProvider.module.scss';

const initialCanvasContext = {
  handleCanvasRef: null,
  webGlMultiplexedContextState: null,
};

const ThreeJsMultiplexedCanvasContext = createContext(initialCanvasContext);

const createMultiplexedCanvasHelpers = (canvasEl) => {
  let canvases = [];

  const clock = new Clock();
  const renderer = new WebGLRenderer({
    canvas: canvasEl,
    precision: 'lowp',
    antialias: false,
    alpha: true,
    // outputEncoding: sRGBEncoding,
  });

  const pixelRatio = Math.max(1.4, window?.devicePixelRatio || 0);

  renderer.setPixelRatio(pixelRatio);

  const addThreeJsCanvas = (fauxCanvasEl, renderCanvasScene) =>
    canvases.push({
      fauxCanvasEl,
      renderCanvasScene,
    });

  const removeThreeJsCanvas = (fauxCanvasEl) => {
    canvases = canvases.filter((c) => c.fauxCanvasEl !== fauxCanvasEl);
  };

  const resizeRendererToDisplaySize = (renderer) => {
    const canvas = renderer.domElement;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const needResize = canvas.width !== width || canvas.height !== height;
    if (needResize) {
      renderer.setSize(width, height, false);
    }

    return needResize;
  };

  const globalRender = (
    timeOverride = null,
    clearColor = new Color('#ff0000')
  ) => {
    const time = timeOverride || clock.getElapsedTime();

    resizeRendererToDisplaySize(renderer);

    renderer.setScissorTest(false);
    renderer.setClearColor(clearColor, 0);
    renderer.clear(true, true);
    renderer.setScissorTest(true);

    const transform = `translateY(${window.scrollY}px)`;
    renderer.domElement.style.transform = transform;

    for (const { fauxCanvasEl, renderCanvasScene } of canvases) {
      const dims = fauxCanvasEl.getBoundingClientRect();
      const { left, right, top, bottom, width, height } = dims;

      const isOffscreen =
        bottom < 0 ||
        top > renderer.domElement.clientHeight ||
        right < 0 ||
        left > renderer.domElement.clientWidth;

      if (!isOffscreen) {
        const positiveYUpBottom = renderer.domElement.clientHeight - bottom;

        const scissorOffsets = [left, positiveYUpBottom];

        renderer.setScissor(left, positiveYUpBottom, width, height);
        renderer.setViewport(left, positiveYUpBottom, width, height);

        renderCanvasScene(time, dims, renderer, scissorOffsets);
      }
    }

    requestAnimationFrame(globalRender);
  };

  return {
    addThreeJsCanvas,
    removeThreeJsCanvas,
    globalRender,
    renderer,
  };
};

const ThreeJsMultiplexedCanvasProvider = ({ children }) => {
  const [canvas, setCanvas] = useState(null);
  const handleCanvasRef = useCallback((canvasEl) => {
    setCanvas(canvasEl);
  }, []);

  const [webGlMultiplexedContextState, setwebGlMultiplexedContextState] =
    useState({});

  useEffect(() => {
    if (!canvas) {
      return;
    }

    setwebGlMultiplexedContextState({
      ...createMultiplexedCanvasHelpers(canvas),
      canvas,
    });
  }, [canvas]);

  useEffect(() => {
    if (!('globalRender' in webGlMultiplexedContextState)) {
      return;
    }

    // trigger main multiplexed raf render loop
    // this will render all scenes added with webGlMultiplexedContextState.addThreeJsCanvas()
    // if they're visible
    webGlMultiplexedContextState.globalRender();
  }, [webGlMultiplexedContextState]);

  return (
    <ThreeJsMultiplexedCanvasContext.Provider
      value={webGlMultiplexedContextState}>
      <canvas ref={handleCanvasRef} className={s.Canvas}></canvas>
      {children}
    </ThreeJsMultiplexedCanvasContext.Provider>
  );
};

ThreeJsMultiplexedCanvasProvider.propTypes = {};
ThreeJsMultiplexedCanvasProvider.defaultProps = {};

export default ThreeJsMultiplexedCanvasProvider;
export { ThreeJsMultiplexedCanvasContext };
